import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Users, TrendUp, CheckCircle, ArrowRight, Package, Sparkle } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../lib/api";
import TapTrustBadge from "../components/TapTrustBadge";
import { formatCurrency } from "../lib/currency";

const fmt$ = (n, cur) => formatCurrency(n, cur || "USD");

const TIER_ORDER = ["STARTER", "TEAM", "BUSINESS", "ENTERPRISE"];

function TierCard({ tier, activeSeats }) {
  const isActive = activeSeats != null &&
    activeSeats >= tier.min &&
    (tier.max === null || activeSeats <= tier.max);
  return (
    <div className={`border p-4 ${isActive ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200"}`}>
      <div className={`font-mono text-[10px] tracking-[0.18em] ${isActive ? "text-[#FF3E00]" : "text-zinc-500"}`}>
        {tier.label}
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${isActive ? "text-white" : "text-zinc-950"}`}>
        {tier.discount_pct > 0 ? `−${tier.discount_pct}%` : "Full price"}
      </div>
      <div className={`text-[11px] mt-1 ${isActive ? "text-zinc-300" : "text-zinc-500"}`}>
        {tier.min}{tier.max ? `–${tier.max}` : "+"} seats
      </div>
      {isActive && (
        <div className="mt-2 font-mono text-[9px] tracking-[0.15em] text-[#FF3E00] uppercase inline-flex items-center gap-1">
          <CheckCircle size={10} weight="fill" /> YOUR TIER
        </div>
      )}
    </div>
  );
}

export default function BillingSeats() {
  const [tiers, setTiers] = useState([]);
  const [basePrice, setBasePrice] = useState(39);
  const [seats, setSeats] = useState(10);
  const [quote, setQuote] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  // Check ?checkout=success / cancel on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success(`Purchased ${params.get("seats") || ""} seats — you're good to invite more teammates!`);
      window.history.replaceState({}, "", "/billing/seats");
    } else if (params.get("checkout") === "cancel") {
      toast.info("Checkout cancelled.");
      window.history.replaceState({}, "", "/billing/seats");
    }
  }, []);

  useEffect(() => {
    api.get("/billing/seats/tiers").then((r) => {
      setTiers(r.data.tiers || []);
      setBasePrice(r.data.base_price_per_seat_usd || 39);
    });
    api.get("/tenant").then((r) => setCurrent(r.data.subscription || null)).catch(() => null);
  }, []);

  const refreshQuote = useCallback(async (n) => {
    if (!n || n < 1) { setQuote(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/billing/seats/quote?seats=${n}`);
      setQuote(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
      setQuote(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => refreshQuote(seats), 300);
    return () => clearTimeout(t);
  }, [seats, refreshQuote]);

  const buy = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/tap/charges/checkout", { intent: "seats", seats });
      if (data.transaction_url) {
        window.location.href = data.transaction_url;
      } else {
        toast.error("Tap did not return a redirect URL. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setCheckingOut(false); }
  };

  const orderedTiers = useMemo(
    () => [...tiers].sort((a, b) => TIER_ORDER.indexOf(a.label) - TIER_ORDER.indexOf(b.label)),
    [tiers]
  );

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="billing-seats-page">
      <div className="border-b border-zinc-950 pb-6">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">/ ORG · BILLING</div>
        <h1 className="font-display font-black text-4xl tracking-tight mt-2">Buy more seats</h1>
        <p className="text-zinc-600 text-sm mt-2 max-w-2xl">
          Add seats to your subscription. Bulk discounts apply automatically — the more you buy, the less each seat costs.
        </p>
      </div>

      {/* Current status */}
      {current && (() => {
        const isUnlimited = current.seats_purchased == null;
        const purchasedLabel = isUnlimited ? "∞" : current.seats_purchased;
        const availableLabel = isUnlimited ? "∞" : (current.seats_available ?? 0);
        const availableTone = !isUnlimited && (current.seats_available || 0) === 0 ? "text-[#D0021B]" : "";
        const capLabel = current.max_users == null ? "∞" : current.max_users;
        return (
        <div className="mt-6 border border-zinc-200 p-5 bg-zinc-50 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="billing-current-status">
          <div>
            <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Paid seats</div>
            <div className="font-display font-black text-2xl tnum mt-1" data-testid="billing-seats-paid">{purchasedLabel}</div>
            <div className="text-[10px] font-mono text-zinc-400 mt-0.5" data-testid="billing-seats-cap">Plan cap · {capLabel}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">In use</div>
            <div className="font-display font-black text-2xl tnum mt-1" data-testid="billing-seats-inuse">{current.seats_used ?? 0}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Available</div>
            <div className={`font-display font-black text-2xl tnum mt-1 ${availableTone}`} data-testid="billing-seats-available">
              {availableLabel}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Base price / seat</div>
            <div className="font-display font-black text-2xl tnum mt-1">{fmt$(basePrice)}<span className="text-xs font-normal text-zinc-500">/mo</span></div>
          </div>
        </div>
        );
      })()}

      {/* Discount tier grid */}
      <div className="mt-6">
        <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase mb-3">Bulk discount tiers</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {orderedTiers.map((t) => (
            <TierCard key={t.label} tier={t} activeSeats={seats} />
          ))}
        </div>
      </div>

      {/* Buy panel */}
      <div className="mt-6 border border-zinc-950 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr]">
        <div className="p-6 bg-zinc-50 border-r border-zinc-200">
          <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">How many seats?</div>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="number" min="1" max="1000"
              value={seats}
              onChange={(e) => setSeats(Math.max(1, Math.min(1000, parseInt(e.target.value, 10) || 1)))}
              data-testid="billing-seats-input"
              className="font-display font-black text-5xl tnum w-32 border-b-2 border-zinc-950 bg-transparent outline-none focus:border-[#FF3E00]"
            />
            <div>
              <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">seats</div>
              <div className="text-[11px] text-zinc-500">monthly, cancel anytime</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[5, 10, 25, 50, 100].map((n) => (
              <button
                key={n}
                onClick={() => setSeats(n)}
                data-testid={`billing-preset-${n}`}
                className={`font-mono text-[10px] tracking-[0.12em] uppercase border px-3 py-1.5 transition-colors ${seats === n ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}
              >
                {n} SEATS
              </button>
            ))}
          </div>
        </div>

        <div className="p-6" data-testid="billing-quote-panel">
          {loading && <div className="font-mono text-xs text-zinc-400 pulse-dot">CALCULATING…</div>}
          {!loading && quote && (
            <>
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">/ QUOTE · {quote.tier_label}</div>
                {quote.discount_pct > 0 && (
                  <div className="font-mono text-[10px] tracking-[0.15em] bg-emerald-100 text-emerald-800 px-2 py-0.5 border border-emerald-200">
                    −{quote.discount_pct}% DISCOUNT
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-display font-black text-6xl tnum" data-testid="quote-total">{fmt$(quote.total)}</div>
                <div className="text-zinc-500">/ month</div>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {quote.seats} seats × {fmt$(quote.per_seat_after_discount)}/seat
                {quote.savings > 0 && (
                  <span className="text-emerald-700 font-bold ml-2">
                    · save {fmt$(quote.savings)}/mo
                  </span>
                )}
              </div>
              <div className="mt-4 border-t border-zinc-200 pt-4 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Subtotal (base)</span><span className="tnum">{fmt$(quote.subtotal)}</span></div>
                {quote.savings > 0 && (
                  <div className="flex justify-between text-emerald-700 font-semibold">
                    <span>Bulk discount ({quote.discount_pct}%)</span>
                    <span className="tnum">−{fmt$(quote.savings)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-zinc-200 pt-1 font-bold">
                  <span>You pay monthly</span><span className="tnum">{fmt$(quote.total)}</span>
                </div>
                <div className="text-[11px] text-zinc-500 pt-1">
                  After purchase you&apos;ll have <strong>{quote.will_be_after_purchase}</strong> total seats
                  {current?.seats_used != null && (
                    <> ({Math.max(0, quote.will_be_after_purchase - current.seats_used)} available for new invitations)</>
                  )}
                </div>
              </div>
              <button
                onClick={buy}
                disabled={checkingOut}
                data-testid="billing-buy-btn"
                className="mt-5 w-full bg-[#FF3E00] text-white font-mono text-xs tracking-[0.15em] uppercase py-4 hover:bg-zinc-950 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                {checkingOut ? "REDIRECTING TO TAP…" : (<><Package size={14} /> BUY {seats} SEAT{seats > 1 ? "S" : ""} · {fmt$(quote.total)}/MO <ArrowRight size={14} weight="bold" /></>)}
              </button>
              <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1.5" data-testid="billing-checkout-footnote">
                <Sparkle size={11} weight="fill" className="text-[#FF3E00]" />
                Secure checkout via Tap Payments. Cancel anytime from Org · Billing.
              </div>
              <TapTrustBadge className="mt-2" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
